<template>
  <form class="form-withdraw">
    <atomic-skeletor v-if="isLoadingSupportedCurrencies" class="form-withdraw__skeletor" />

    <template v-else>
      <div class="form-withdraw__wrapper">
        <!--        Balance-->
        <wallet-currency-selector
          class="form-withdraw__balance"
          v-model:current-currency-id="currentCurrencyId"
          :balance-object="balanceObject"
          :list-width="contentWidth"
          :filteredCurrencyList="filteredCurrencyList"
        />

        <!--        Network-->
        <wallet-networks-selector
          v-if="showNetworkSelector"
          class="form-withdraw__networks"
          v-model:network-id="networkId"
          :network-select-options="networkSelectOptions"
          :list-width="contentWidth"
          @setNetwork="setNetwork"
        />
      </div>

      <wallet-balance-info :balance-object="balanceObject" :active-account="activeAccount" />
    </template>

    <template v-if="showCanWithdrawal">
      <div
        class="form-withdraw__content"
        ref="formContentRef"
        :class="{ 'form-withdraw__content_disabled': isFormDisabled }"
      >
        <p v-if="isFormDisabled">
          {{ getContent(popupsData, defaultLocalePopupsData, 'wallet.withdraw.selectNetwork') }}
        </p>

        <form-input-amount-withdrawal
          v-model="amountValue"
          :amount-max="formatAmountMax"
          :amount-min="formatAmountMin"
          :label="getContent(popupsData, defaultLocalePopupsData, 'wallet.withdraw.sumLabel')"
          :active-account="activeAccount"
          :method="method"
        />

        <wallet-withdrawal-fields
          v-if="fields?.length"
          :key="method"
          v-model:withdraw-form-data="withdrawFormData"
          v-model:is-validate-error="isFormValidateError"
          :server-form-errors="serverFormErrors"
          :fields="fields"
          :selected-network-data-name="selectedNetworkData?.name"
          :method-name="method"
          :active-currency="activeAccount?.currency"
          :network-id="networkId"
        />
      </div>

      <list-manual-providers
        v-if="providers.length && !isLoadingProviders"
        :providers="providers"
        v-model:active-provider="currentProvider"
      />

      <atomic-skeleton-card v-if="isLoadingProviders" :item-count="3" />

      <button-base
        type="primary"
        size="md"
        :isDisabled="buttonDisabled"
        :is-processing="isSending"
        @click="getWithdraw"
      >
        {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.withdraw') }}
        {{ buttonAmount }}
        {{ formatAmountMin.currency }}
      </button-base>
    </template>

    <wallet-cant-withdrawal v-else />
  </form>
</template>

<script setup lang="ts">
  import { PayMethod } from '@skeleton/consts/method';
  import { ModalName } from '@skeleton/consts/modals';

  import type { IPaymentField, IPaymentGroup, IPaymentMethod } from '@skeleton/core/types';

  const networkId = ref<string | 'native'>();
  const isFormValidateError = ref(false);
  const formContentRef = ref<HTMLElement | null>(null);
  const contentWidth = ref<string>('');

  const props = defineProps<{
    amountMax: number;
    amountMin: number;
    fields: IPaymentField[];
    method: string;
    methodId: string;
    changeMobileForm: () => void;
  }>();

  const isSending = ref(false);
  const withdrawFormData = ref<{ [key: string]: string }>({});

  const {
    popupsData,
    defaultLocalePopupsData,
    alertsData,
    defaultLocaleAlertsData,
    layoutData,
    defaultLocaleLayoutData,
  } = storeToRefs(useGlobalStore());

  const { showAlert } = useLayoutStore();
  const { closeModalSync } = useModalStore();
  const { formatBalance, getMainBalanceFormat, getContent, localizePath } = useProjectMethods();
  const {
    currencyList,
    supportedTransactionAccounts,
    balanceObject,
    currentCurrencyId,
    isLoadingSupportedCurrencies,
    selectedCurrency,
  } = useTransactionOperations(props);

  const { providers, getProviders, currentProvider, isLoadingProviders } = useManualAgentMethods({
    type: 'withdrawal',
  });

  const transactionStore = useTransactionStore();
  const { withdrawMethods } = storeToRefs(transactionStore);

  const filteredCurrencyList = computed(() => {
    return currencyList.value?.filter(item => {
      const foundAccount = supportedTransactionAccounts.value.find(account => account.currency === item.name);
      return foundAccount?.withdrawalBalance || foundAccount?.id === currentCurrencyId.value;
    });
  });

  const activeAccount = computed(() => {
    return supportedTransactionAccounts.value?.find(account => account.currency === selectedCurrency.value);
  });

  const showCanWithdrawal = computed(() => {
    const account = activeAccount.value;
    return !account || !(account.balance > 0 && !account.withdrawalBalance);
  });

  const networkSelectOptions = computed(() => {
    const method: IPaymentMethod = withdrawMethods.value
      .flatMap((group: IPaymentGroup) => group.paymentMethods)
      .find((paymentMethod: IPaymentMethod) => paymentMethod.method === props.method);

    const select = method?.fields.find((field: IPaymentField) => field.key === 'crypto_network');

    if (!select?.options) {
      return [];
    }

    return select.options;
  });

  const selectedNetworkData = computed(() => {
    return networkSelectOptions.value.find(option => {
      if (option.id) return option.id === networkId.value;
      return networkId.value === 'native';
    });
  });

  const showNetworkSelector = computed(() => {
    return !!networkSelectOptions.value?.length;
  });

  const isFormDisabled = computed(() => {
    return showNetworkSelector.value && !networkId.value;
  });

  const formatAmountMax = computed(() => {
    return formatBalance(activeAccount.value?.currency, selectedNetworkData.value?.maxAmount ?? props.amountMax);
  });

  const formatAmountMin = computed(() => {
    return formatBalance(activeAccount.value?.currency, selectedNetworkData.value?.minAmount ?? props.amountMin);
  });

  const amountValue = ref(formatAmountMin.value.amount);

  const serverFormErrors = ref<{ [key: string]: Maybe<string> }>({});

  const buttonAmount = computed(() => {
    if (amountValue.value > formatAmountMax.value.amount) return formatAmountMax.value.amount;
    if (amountValue.value < formatAmountMin.value.amount) return formatAmountMin.value.amount;
    return amountValue.value;
  });

  const activeAccountWithdrawalFormat = computed(() =>
    formatBalance(activeAccount.value?.currency, activeAccount.value?.withdrawalBalance)
  );

  const buttonDisabled = computed(
    () =>
      isFormDisabled.value ||
      isFormValidateError.value ||
      amountValue.value > activeAccountWithdrawalFormat.value.amount ||
      amountValue.value < formatAmountMin.value.amount ||
      amountValue.value > formatAmountMax.value.amount ||
      (props.method === PayMethod.MANUAL_AGENT_WITHDRAW && !currentProvider.value) ||
      isSending.value
  );

  const setNetwork = async (code: string) => {
    networkId.value = code;
    amountValue.value = formatAmountMin.value.amount;
  };

  const getWithdraw = async (): Promise<void> => {
    if (buttonDisabled.value) return;

    const requestFormData = {
      ...withdrawFormData.value,
      phone: withdrawFormData.value.phone ? `+${withdrawFormData.value.phone}` : undefined,
    };

    const networkValue = networkId.value === 'native' ? null : networkId.value;

    const fields: any = {
      ...requestFormData,
    };

    if (props.method === PayMethod.WITHDRAW_WALLET_DIRECT_METHOD) {
      fields.crypto_network = networkValue;
    }

    const mainCurrencyAmount = getMainBalanceFormat(
      activeAccountWithdrawalFormat.value.currency,
      Number(amountValue.value)
    );

    if (props.method === PayMethod.MANUAL_AGENT_WITHDRAW) {
      fields.paymentMethodId = currentProvider.value?.id?.toString();
      fields.amount = mainCurrencyAmount.amount;
    }

    const frogoFingerprint = await useFrogoFingerprintId();

    const params = {
      method: props.method,
      methodId: props.methodId,
      currency: activeAccount.value?.currency || '',
      amount: mainCurrencyAmount.amount,
      accountId: activeAccount.value?.id || '',
      fields,
      frogoFingerprint,
    };

    const { withdrawAccount } = useCoreWalletApi();

    try {
      isSending.value = true;
      await withdrawAccount(params);
      showAlert(
        alertsData.value?.wallet?.withdrawalProcessed || defaultLocaleAlertsData.value?.wallet?.withdrawalProcessed,
        'waiting'
      );
      closeModalSync(ModalName.WALLET);
      await navigateTo('/');
    } catch (err: any) {
      if (err.response?.status === 422) {
        serverFormErrors.value = err.data?.error?.fields;
      } else if (err.data?.error?.code === 13108) {
        const content =
          alertsData.value?.wallet?.withdrawErrorPhoneNotVerified ||
          defaultLocaleAlertsData.value?.wallet?.withdrawErrorPhoneNotVerified;

        showAlert(
          {
            title: content?.title || '',
            description: content?.description?.replace('{accountPageUrl}', localizePath('/profile/info')),
          },
          'error'
        );
      } else {
        showAlert(
          alertsData.value?.global?.somethingWrong || defaultLocaleAlertsData.value?.global?.somethingWrong,
          'error'
        );
      }
    } finally {
      isSending.value = false;
    }
  };

  watch(
    () => currentCurrencyId.value,
    (newVal, oldVal) => {
      if (oldVal && newVal !== oldVal) {
        const currency = supportedTransactionAccounts.value.find(account => account.id === newVal)?.currency;
        transactionStore.getWithdrawMethods(currency);
      }
    }
  );

  watch(
    () => networkSelectOptions.value,
    newVal => {
      if (newVal?.length) {
        networkId.value = undefined;
      }
    }
  );

  watch(
    () => formatAmountMin.value,
    newVal => {
      amountValue.value = newVal?.amount;
    }
  );

  onBeforeMount(async () => {
    if (props.method === PayMethod.MANUAL_AGENT_WITHDRAW) {
      await getProviders();
    }
  });

  const updateContentWidth = () => {
    if (formContentRef.value) {
      contentWidth.value = `${formContentRef.value.offsetWidth}px`;
    }
  };

  onMounted(() => {
    updateContentWidth();
    window.addEventListener('resize', updateContentWidth);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateContentWidth);
  });
</script>

<style src="~/assets/styles/components/form/withdraw.scss" lang="scss" />
